import { Button, Checkbox, ConfigProvider, Input, Modal, Select } from "antd";
import { ReactComponent as Logo } from "asset/menu/logo.svg";
import axios from "axios";
import FindPassword from "components/account/findPassword";
import UpdatePassword from "components/account/updatePassword";
import modalStyle from "css/modal.module.css";
import style from "css/sign.module.css";
import OpenModal from "function/openModal";
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { Cookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { insertAllowPageInfo } from "redux/slices/allowPageSlice";
import { insertUserInfo } from "redux/slices/userSlice";
import CryptoJS from "crypto-js";

/**
 * 파일명: newSignIn.js
 * 작성자: 임창현
 * 작성일자: 25.10.31
 * 설명: SaaS형 로그인
 */
function NewSignIn() {
    //state
    const [pageType, setPageType] = useState("ID");
    const [loading, setLoading] = useState(false);
    const [isCheck, setIsCheck] = useState(false);
    const [user, setUser] = useState({
        id: "",
        pw: "",
    });
    const [authCode, setAuthCode] = useState("");
    const [passwordType, setPasswordType] = useState("INSERT");

    // language
    const { t, i18n } = useTranslation();
    const languageList = [
        { value: "ko-KR", label: t("LANGUAGE.ko") },
        { value: "en-US", label: t("LANGUAGE.en") },
    ];
    const [selectType, setSelectType] = useState(i18n.language);

    //navigate
    const navigate = useNavigate();

    //dispatch
    const dispatch = useDispatch();

    //cookies
    const cookies = new Cookies();

    //ref
    const pwRef = useRef();
    const idRef = useRef();
    const iniPwRef_1 = useRef();
    const iniPwRef_2 = useRef();
    const authCodeRef = useRef();

    //selector
    const userPayload = useSelector((state) => state.user);

    //env
    const authNm = process.env.REACT_APP_AUTH_TOKEN;
    const refreshNm = process.env.REACT_APP_REFRESH_TOKEN;
    const refreshTime = process.env.REACT_APP_REFRESH_TOKEN_TIME;
    const domain = process.env.REACT_APP_DOMAIN;

    const openUpdatePassword = useCallback(() => {
        Modal.info({
            icon: null,
            centered: true,
            title: (
                <p className={modalStyle.modal_title}>
                    {t("LABEL.TITLE.RESET_PASSWORD")}
                </p>
            ),
            content: (
                <div className={modalStyle.modal_content}>
                    <UpdatePassword openUpdatePassword={openUpdatePassword} />
                </div>
            ),
            autoFocusButton: null,
            okButtonProps: {
                style: {
                    display: "none",
                },
            },
        });
    }, [t]);

    useLayoutEffect(() => {
        const email = new URL(window.location.href).searchParams.get("email");
        const authcode = new URL(window.location.href).searchParams.get("authcode");

        const isEmail = email !== undefined && email !== null && email !== "";
        const isAuthcode =
            authcode !== undefined && authcode !== null && authcode !== "";

        if (isEmail && isAuthcode) {
            const data = {
                email: email,
                authcode: authcode,
            };

            axios
                .post("/user/password/authcode", data)
                .then((res) => {
                    if (res.status === 200) {
                        openUpdatePassword();
                    }
                })
                .catch((err) => {
                    const props = {
                        type: "info",
                        title: t("LABEL.TITLE.MESSAGE"),
                        content: t("MESSAGE." + err.response.data),
                        okText: t("common.button.ok"),
                        img:
                            process.env.REACT_APP_CLIENT_URL +
                            "img/common/modal_icon_green.png",
                        btnEvent: () =>
                            (window.location.href = process.env.REACT_APP_CLIENT_URL),
                    };
                    OpenModal(props);
                });
        }
    }, [openUpdatePassword, t]);

    useEffect(() => {
        if (userPayload.name !== "") {
            navigate(process.env.REACT_APP_CLIENT_URL + "app/mypage/schedule");
        }
    }, [navigate, userPayload.name]);

    useEffect(() => {
        return () => setLoading(false);
    }, []);

    // 버튼 handler
    const loginHandler = () => {
        setLoading(true);
        if (pageType === "ID") {
            // 계정 존재여부 확인
            const data = {
                // MEMBER_ID: user.id,
                MEMBER_ID: CryptoJS.AES.encrypt(
                    user.id,
                    CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
                    {
                        iv: CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
                        padding: CryptoJS.pad.Pkcs7,
                        mode: CryptoJS.mode.CBC,
                    }
                ).toString(),
            };

            axios
                .post("/user/login/checkId", data)
                .then((res) => {
                    if (res.status === 200) {
                        setLoading(false);
                        if (res.data.ID_STATUS === 0) {
                            const props = {
                                type: "info",
                                title: t("LABEL.TITLE.MESSAGE"),
                                content: t("MESSAGE.NO_ACCOUNT"),
                                okText: t("common.button.ok"),
                                img:
                                    process.env.REACT_APP_CLIENT_URL +
                                    "img/common/modal_icon_green.png",
                                btnEvent: () => idRef.current.focus(),
                            };
                            OpenModal(props);
                        } else if (res.data.ID_STATUS === 1) {
                            sendAuthMail("", "INSERT");
                            // setPageType("AUTH");
                        } else if (res.data.ID_STATUS === 2) {
                            setPageType("PW");
                            // pwRef.current.focus();
                        }
                    }
                })
                .catch((err) => {
                    const props = {
                        type: "info",
                        title: t("LABEL.TITLE.MESSAGE"),
                        content: t("MESSAGE." + err.response.data),
                        okText: t("common.button.ok"),
                        img:
                            process.env.REACT_APP_CLIENT_URL +
                            "img/common/modal_icon_green.png",
                        btnEvent: () =>
                            (window.location.href = process.env.REACT_APP_CLIENT_URL),
                    };
                    OpenModal(props);
                });
        } else if (pageType === "PW") {
            // 로그인
            // const data = {
            //     MEMBER_ID: user.id,
            //     MEMBER_PW: user.pw,
            // };
            const data = {
                MEMBER_ID: CryptoJS.AES.encrypt(
                    user.id,
                    CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
                    {
                        iv: CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
                        padding: CryptoJS.pad.Pkcs7,
                        mode: CryptoJS.mode.CBC,
                    }
                ).toString(),
                MEMBER_PW: CryptoJS.AES.encrypt(
                    user.pw,
                    CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
                    {
                        iv: CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
                        padding: CryptoJS.pad.Pkcs7,
                        mode: CryptoJS.mode.CBC,
                    }
                ).toString(),
            };

            axios
                .post("/user/login", data)
                .then((res) => {
                    if (res.status === 200) {
                        const memeberInfo = res.data.MEMBER_INFO;

                        cookies.set(authNm, memeberInfo.AUTH_TOKEN, {
                            path: "/",
                            // maxAge: isCheck ? authTime : null,
                            domain: domain,
                        });

                        cookies.set(refreshNm, memeberInfo.REFRESH_TOKEN, {
                            path: "/",
                            maxAge: isCheck ? refreshTime : null,
                            domain: domain,
                        });

                        dispatch(
                            insertUserInfo({
                                name: memeberInfo.MEMBER_NM,
                                id: memeberInfo.MEMBER_ID,
                                zoomId: memeberInfo.ZOOM_ID,
                            })
                        );

                        localStorage.setItem("noticeAlert", "true"); // 메인페이지 공지사항 표출

                        const allowPages = res.data.ALLOW_PAGES;

                        dispatch(insertAllowPageInfo({ pages: allowPages }));
                        navigate(process.env.REACT_APP_CLIENT_URL + "app/mypage/schedule");
                    }
                })
                .catch((err) => {
                    const props = {
                        type: "info",
                        title: t("LABEL.TITLE.MESSAGE"),
                        content: t("MESSAGE." + err.response.data),
                        okText: t("common.button.ok"),
                        img:
                            process.env.REACT_APP_CLIENT_URL +
                            "img/common/modal_icon_green.png",
                        btnEvent: () => setLoading(false),
                    };
                    OpenModal(props);
                });
        } else if (pageType === "SET_PW") {     // 새로운 계정 비밀번호 설정
            // 비밀번호 유효성 체크
            if (user.pw !== user.confirmPw) {
                const props = {
                    type: "info",
                    title: t("LABEL.TITLE.MESSAGE"),
                    content: t("MESSAGE.PASSWORD_MISMATCH"),
                    okText: t("common.button.ok"),
                    img:
                        process.env.REACT_APP_CLIENT_URL +
                        "img/common/modal_icon_green.png",
                    btnEvent: () => {
                        setLoading(false);
                        iniPwRef_2.current.focus();
                    },
                };
                OpenModal(props);
                return;
            } else if (!checkPasswordValidity(user.pw)) {
                const props = {
                    type: "info",
                    title: t("LABEL.TITLE.MESSAGE"),
                    content: t("MESSAGE.PW_VALIDITY"),
                    okText: t("common.button.ok"),
                    img:
                        process.env.REACT_APP_CLIENT_URL +
                        "img/common/modal_icon_green.png",
                    btnEvent: () => {
                        setLoading(false);
                        iniPwRef_1.current.focus();
                    },
                };
                OpenModal(props);
                return;
            }

            const data = {
                MEMBER_ID: CryptoJS.AES.encrypt(
                    user.id,
                    CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
                    {
                        iv: CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
                        padding: CryptoJS.pad.Pkcs7,
                        mode: CryptoJS.mode.CBC,
                    }
                ).toString(),
                MEMBER_PW: CryptoJS.AES.encrypt(
                    user.pw,
                    CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
                    {
                        iv: CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
                        padding: CryptoJS.pad.Pkcs7,
                        mode: CryptoJS.mode.CBC,
                    }
                ).toString(),
                TYPE: passwordType,
            };

            axios
                .post("/user/login/setPassword", data)
                .then((res) => {
                    if (res.status === 200) {
                        const props = {
                            type: "info",
                            title: t("LABEL.TITLE.MESSAGE"),
                            content: t("MESSAGE.PW_SET_SUCCESS"),
                            okText: t("common.button.ok"),
                            img:
                                process.env.REACT_APP_CLIENT_URL +
                                "img/common/modal_icon_green.png",
                            btnEvent: () =>
                                (window.location.href = process.env.REACT_APP_CLIENT_URL),
                        };
                        OpenModal(props);
                    }
                })
                .catch((err) => {
                    const props = {
                        type: "info",
                        title: t("LABEL.TITLE.MESSAGE"),
                        content: t("MESSAGE.PW_SET_FAIL"),
                        okText: t("common.button.ok"),
                        img:
                            process.env.REACT_APP_CLIENT_URL +
                            "img/common/modal_icon_green.png",
                        btnEvent: () => {
                            setLoading(false);
                            iniPwRef_2.current.focus();
                        },
                    };
                    OpenModal(props);
                });
        } else if (pageType === "AUTH") {
            // 이메일 인증 코드 확인
            confirmAuth();
        }
    };

    // 인증 코드
    const confirmAuth = () => {
        const config = {
            params: {
                EMAIL: user.id,
                CODE: authCode,
                TYPE: passwordType,
            },
        };

        axios
            .get("/auth/confirm", config)
            .then((res) => {
                if (res.status === 200) {
                    if (res.data.IS_MATCH) {
                        setLoading(false);
                        setPageType("SET_PW");
                    } else {
                        const props = {
                            type: "info",
                            title: t("LABEL.TITLE.MESSAGE"),
                            content: t("MESSAGE.CHECK_AUTHCODE"),
                            okText: t("common.button.ok"),
                            img:
                                process.env.REACT_APP_CLIENT_URL +
                                "img/common/modal_icon_green.png",
                            btnEvent: () => {
                                setLoading(false);
                                authCodeRef.current.focus()
                            },
                        };
                        OpenModal(props);
                    }
                }
            })
            .catch(() => {
                const props = {
                    type: "info",
                    title: t("LABEL.TITLE.MESSAGE"),
                    content: t("MESSAGE.AUTHCODE_FAIL"),
                    okText: t("common.button.ok"),
                    img:
                        process.env.REACT_APP_CLIENT_URL +
                        "img/common/modal_icon_green.png",
                    btnEvent: () => {
                        setLoading(false);
                        authCodeRef.current.focus()
                    },
                };
                OpenModal(props);
            });
    };

    const bodyRender = (type) => {
        switch (type) {
            case "ID":
                return (
                    <div className={style.item}>
                        <p className={style.item_title}>User ID</p>
                        <ConfigProvider
                            theme={{
                                components: {
                                    Input: {
                                        colorBgContainer: "transparent",
                                        colorTextPlaceholder: "white",
                                        colorText: "white",
                                    },
                                },
                            }}
                        >
                            <Input
                                ref={idRef}
                                className={style.item_input}
                                placeholder={t("PLACEHOLDER.INPUT.ENTER_ID")}
                                // disabled={loading}
                                onChange={(e) => setUser({ ...user, id: e.target.value })}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        // pwRef.current.focus();
                                        loginHandler();
                                    }
                                }}
                            />
                        </ConfigProvider>
                    </div>
                );

            case "PW":
                return (
                    <div className={style.item}>
                        <p className={style.item_title}>Password</p>
                        <ConfigProvider
                            theme={{
                                components: {
                                    Input: {
                                        colorBgContainer: "transparent",
                                        colorTextPlaceholder: "white",
                                        colorText: "white",
                                        colorIcon: "white",
                                        colorIconHover: "white",
                                    },
                                },
                            }}
                        >
                            <Input.Password
                                ref={pwRef}
                                className={style.item_input}
                                placeholder={t("PLACEHOLDER.INPUT.ENTER_PW")}
                                // disabled={loading}
                                onChange={(e) => setUser({ ...user, pw: e.target.value })}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        loginHandler();
                                    }
                                }}
                            />
                        </ConfigProvider>
                    </div>
                );

            case "SET_PW":
                return (
                    <>
                        <div className={style.item}>
                            <p className={style.item_title}>Password</p>
                            <ConfigProvider
                                theme={{
                                    components: {
                                        Input: {
                                            colorBgContainer: "transparent",
                                            colorTextPlaceholder: "white",
                                            colorText: "white",
                                            colorIcon: "white",
                                            colorIconHover: "white",
                                        },
                                    },
                                }}
                            >
                                <Input.Password
                                    ref={iniPwRef_1}
                                    className={style.item_input}
                                    placeholder={t("PLACEHOLDER.INPUT.ENTER_PW")}
                                    // disabled={loading}
                                    onChange={(e) => setUser({ ...user, pw: e.target.value })}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            iniPwRef_2.current.focus();
                                        }
                                    }}
                                />
                            </ConfigProvider>
                        </div>
                        <div className={style.item}>
                            <p className={style.item_title}>Confirm Password</p>
                            <ConfigProvider
                                theme={{
                                    components: {
                                        Input: {
                                            colorBgContainer: "transparent",
                                            colorTextPlaceholder: "white",
                                            colorText: "white",
                                            colorIcon: "white",
                                            colorIconHover: "white",
                                        },
                                    },
                                }}
                            >
                                <Input.Password
                                    ref={iniPwRef_2}
                                    className={style.item_input}
                                    placeholder={t("PLACEHOLDER.INPUT.ENTER_CONFIRM_PW")}
                                    // disabled={loading}
                                    onChange={(e) =>
                                        setUser({ ...user, confirmPw: e.target.value })
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            loginHandler();
                                        }
                                    }}
                                />
                            </ConfigProvider>
                        </div>
                    </>
                );

            case "AUTH":
                return (
                    <div className={style.item}>
                        <p className={style.item_title}>
                            {t("LABEL.TITLE.VERIFICATION_CODE")}
                        </p>
                        <ConfigProvider
                            theme={{
                                components: {
                                    Input: {
                                        colorBgContainer: "transparent",
                                        colorTextPlaceholder: "white",
                                        colorText: "white",
                                    },
                                },
                            }}
                        >
                            <Input
                                ref={authCodeRef}
                                className={style.item_input}
                                value={authCode}
                                placeholder={t("PLACEHOLDER.INPUT.ENTER_CODE")}
                                // disabled={loading}
                                onChange={(e) => setAuthCode(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        loginHandler();
                                    }
                                }}
                            />
                        </ConfigProvider>
                    </div>
                );

            // case "COMPLETE":
            //     return (
            //         <div className={style.item}>
            //             <p className={style.item_title}>
            //                 게정 설정이 완료되었습니다.
            //                 다시 로그인을 시도해주세요.
            //             </p>
            //         </div>
            //     );
            default:
                return <></>;
        }
    };

    // 인증 코드 전송
    const sendAuthMail = (mailType, pwType) => {
        setLoading(true);

        if (checkEmailFormat(user.id)) {
            const params = {
                type: pwType,
                to: user.id,
            };

            axios
                .post("/auth/sendAuthMail", params)
                .then((res) => {
                    setLoading(false);
                    setPageType("AUTH");
                    const props = {
                        type: "info",
                        title: t("LABEL.TITLE.MESSAGE"),
                        content: t(`MESSAGE.AUTHCODE_${mailType}SEND_SUCCESS`, { ID: user.id }),
                        okText: t("common.button.ok"),
                        img:
                            process.env.REACT_APP_CLIENT_URL +
                            "img/common/modal_icon_green.png",
                        btnEvent: () => authCodeRef.current.focus(),
                    };
                    OpenModal(props);
                })
                .catch(() => {
                    setLoading(false);
                    const props = {
                        type: "info",
                        title: t("LABEL.TITLE.MESSAGE"),
                        content: t("MESSAGE.AUTHCODE_SEND_FAIL"),
                        okText: t("common.button.ok"),
                        img:
                            process.env.REACT_APP_CLIENT_URL +
                            "img/common/modal_icon_green.png",
                        btnEvent: () => authCodeRef.current.focus(),
                    };
                    OpenModal(props);
                });
        } else {
            setLoading(false);
            const props = {
                type: "info",
                title: t("LABEL.TITLE.MESSAGE"),
                content: t("MESSAGE.EMAIL_FORMAT_ERROR"),
                okText: t("common.button.ok"),
                img:
                    process.env.REACT_APP_CLIENT_URL + "img/common/modal_icon_green.png",
                btnEvent: () =>
                    (window.location.href = process.env.REACT_APP_CLIENT_URL),
            };
            OpenModal(props);
            return;
        }
    };

    // 이메일 정규식
    const checkEmailFormat = (email) => {
        var reg =
            /^[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/i;
        return reg.test(email);
    };

    // 비밀번호 복잡도 검사
    const checkPasswordValidity = (password) => {
        const regex =
            /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_\-+=\[{\]};:'",<.>/?\\|`~])[A-Za-z\d!@#$%^&*()_\-+=\[{\]};:'",<.>/?\\|`~]{8,}$/;
        return regex.test(password);
    };

    const passwordHandler = () => {
        // Modal.info({
        //     icon: null,
        //     centered: true,
        //     title: (
        //         <p className={modalStyle.modal_title}>
        //             {t("LABEL.TITLE.CHANGE_PASSWORD")}
        //         </p>
        //     ),
        //     content: (
        //         <div className={modalStyle.modal_content}>
        //             <FindPassword
        //                 sendMailSuccess={sendMailSuccess}
        //                 sendMailFail={sendMailFail}
        //             />
        //         </div>
        //     ),
        //     autoFocusButton: null,
        //     okButtonProps: {
        //         style: {
        //             display: "none",
        //         },
        //     },
        // });
        // setPasswordType("UPDATE");   // 비밀번호 수정 (Update)
        // sendAuthMail("");
    };

    // const sendMailSuccess = (mail) => {
    //     const props = {
    //         type: "info",
    //         title: t("LABEL.TITLE.MESSAGE"),
    //         content: t("MESSAGE.MAIL_SEND_SUCCESS"),
    //         okText: t("common.button.ok"),
    //         img: process.env.REACT_APP_CLIENT_URL + "img/common/modal_icon_green.png",
    //         btnEvent: () => {
    //             setUser({ id: mail });
    //             setPasswordType("U");
    //             setPageType("AUTH");
    //         },
    //     };
    //     OpenModal(props);
    // };

    // const sendMailFail = (err) => {
    //     const props = {
    //         type: "info",
    //         title: t("LABEL.TITLE.MESSAGE"),
    //         content: t("user." + err.response.data),
    //         okText: t("common.button.ok"),
    //         img: process.env.REACT_APP_CLIENT_URL + "img/common/modal_icon_green.png",
    //         btnEvent: () => passwordHandler(),
    //     };
    //     OpenModal(props);
    // };

    return (
        <div className={style.wrapper}>
            <div className={style.container}>
                <section className={style.section}>
                    <ConfigProvider
                        theme={{
                            components: {
                                Select: {
                                    // select area
                                    colorBgContainer: "transparent",
                                    colorText: "white",
                                    // dropdown area
                                    colorBgElevated: "transparent",
                                    controlItemBgActive: "black",
                                    controlItemBgActiveHover: "black",
                                    controlItemBgHover: "black",
                                    // colorIcon: "white"
                                },
                            },
                        }}
                    >
                        <div className="section_select">
                            <Select
                                options={languageList}
                                value={selectType}
                                onChange={(value) => {
                                    setSelectType(value);
                                    i18n.changeLanguage(value);
                                }}
                                className="select-box"
                            />
                        </div>
                    </ConfigProvider>

                    <div>
                        <Logo className={style.logo} />
                        <p className={style.title}>Hybrid working system</p>
                    </div>

                    <div>
                        {bodyRender(pageType)}

                        {pageType === "PW" ? (
                            <div className={style.option_item}>
                                <ConfigProvider
                                    theme={{
                                        components: {
                                            Checkbox: {
                                                colorBgContainer: "transparent",
                                            },
                                        },
                                    }}
                                >
                                    <Checkbox
                                        className={style.chkbox}
                                        checked={isCheck}
                                        onClick={() => setIsCheck(!isCheck)}
                                    >
                                        {t("BTN.KEEP_ME_LOGGED_IN")}
                                    </Checkbox>
                                </ConfigProvider>

                                <p
                                    className={`${style.password} cursor`}
                                    // onClick={() => passwordHandler()}
                                    onClick={() => {
                                        setPasswordType("UPDATE");   // 비밀번호 수정 (Update)
                                        sendAuthMail("", "UPDATE");
                                    }}
                                >
                                    {t("BTN.FORGOT_PW")}
                                </p>
                            </div>
                        ) : pageType === "AUTH" ? (
                            <div
                                style={{
                                    cursor: "pointer",
                                    width: "50px",
                                    textAlign: "center",
                                    color: "#fff",
                                    textDecoration: "underline",
                                    marginBottom: "10px",
                                }}
                                onClick={() => sendAuthMail("RE", passwordType)}
                            >
                                {t("BTN.RESEND")}
                            </div>
                        ) : (
                            <></>
                        )}

                        <Button
                            htmlType="button"
                            className={style.button}
                            type="primary"
                            loading={loading}
                            onClick={() => loginHandler()}
                            style={{
                                fontFamily: "SpoqaHanSansNeo",
                                fontWeight: "600",
                                fontSize: "18px",
                            }}
                        >
                            {
                                pageType === "PW"
                                    ? t("BTN.LOGIN") // "로그인"
                                    : pageType === "SET_PW"
                                        ? t("BTN.SAVE") // "저장"
                                        : t("BTN.NEXT") // "다음" (아이디 입력 & 인증 코드 입력 페이지)
                            }
                        </Button>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default NewSignIn;
